import { useState, useEffect } from 'react';
import api from '../api';

export default function Factories() {
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ nazwa: '', kraj: '', kontakt: '', notatki: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.rola === 'ADMIN';

  useEffect(() => {
    fetchFactories();
  }, []);

  const fetchFactories = async () => {
    try {
      const res = await api.get('/factories');
      setFactories(res.data);
    } catch (err) {
      setError('Błąd pobierania fabryk');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingFactory(null);
    setForm({ nazwa: '', kraj: '', kontakt: '', notatki: '' });
    setError('');
    setShowForm(true);
  };

  const openEdit = (factory) => {
    setEditingFactory(factory);
    setForm({
      nazwa: factory.nazwa,
      kraj: factory.kraj || '',
      kontakt: factory.kontakt || '',
      notatki: factory.notatki || ''
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingFactory(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingFactory) {
        await api.put(`/factories/${editingFactory.id}`, form);
      } else {
        await api.post('/factories', form);
      }
      await fetchFactories();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/factories/${id}`);
      setDeleteConfirm(null);
      await fetchFactories();
    } catch (err) {
      setError('Błąd usuwania fabryki');
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h2 style={s.pageTitle}>Fabryki</h2>
          <p style={s.pageSubtitle}>{factories.length} {factories.length === 1 ? 'fabryka' : 'fabryk'}</p>
        </div>
        {isAdmin && (
          <button style={s.btnPrimary} onClick={openCreate}>
            + Dodaj fabrykę
          </button>
        )}
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      {loading ? (
        <div style={s.loading}>Ładowanie...</div>
      ) : factories.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>🏭</div>
          <p style={s.emptyText}>Brak fabryk</p>
          {isAdmin && (
            <button style={s.btnPrimary} onClick={openCreate}>Dodaj pierwszą fabrykę</button>
          )}
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Nazwa</th>
                <th style={s.th}>Kraj</th>
                <th style={s.th}>Kontakt</th>
                <th style={s.th}>Notatki</th>
                {isAdmin && <th style={{ ...s.th, width: '120px' }}>Akcje</th>}
              </tr>
            </thead>
            <tbody>
              {factories.map((f, i) => (
                <tr key={f.id} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                  <td style={{ ...s.td, fontWeight: '600' }}>{f.nazwa}</td>
                  <td style={s.td}>{f.kraj || <span style={s.empty2}>—</span>}</td>
                  <td style={s.td}>{f.kontakt || <span style={s.empty2}>—</span>}</td>
                  <td style={{ ...s.td, maxWidth: '200px' }}>
                    {f.notatki
                      ? <span style={s.noteText}>{f.notatki}</span>
                      : <span style={s.empty2}>—</span>
                    }
                  </td>
                  {isAdmin && (
                    <td style={s.td}>
                      <div style={s.actions}>
                        <button style={s.btnEdit} onClick={() => openEdit(f)}>Edytuj</button>
                        <button style={s.btnDelete} onClick={() => setDeleteConfirm(f)}>Usuń</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal formularza */}
      {showForm && (
        <div style={s.overlay} onClick={closeForm}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {editingFactory ? 'Edytuj fabrykę' : 'Nowa fabryka'}
              </h3>
              <button style={s.closeBtn} onClick={closeForm}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={s.field}>
                <label style={s.label}>Nazwa *</label>
                <input
                  style={s.input}
                  value={form.nazwa}
                  onChange={e => setForm({ ...form, nazwa: e.target.value })}
                  required
                  placeholder="np. Shenzhen Watch Factory"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Kraj</label>
                <input
                  style={s.input}
                  value={form.kraj}
                  onChange={e => setForm({ ...form, kraj: e.target.value })}
                  placeholder="np. Chiny"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Kontakt</label>
                <input
                  style={s.input}
                  value={form.kontakt}
                  onChange={e => setForm({ ...form, kontakt: e.target.value })}
                  placeholder="email lub telefon"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Notatki</label>
                <textarea
                  style={{ ...s.input, height: '80px', resize: 'vertical' }}
                  value={form.notatki}
                  onChange={e => setForm({ ...form, notatki: e.target.value })}
                  placeholder="Dodatkowe informacje..."
                />
              </div>
              {error && <p style={s.errorText}>{error}</p>}
              <div style={s.modalFooter}>
                <button type="button" style={s.btnCancel} onClick={closeForm}>Anuluj</button>
                <button type="submit" style={s.btnPrimary} disabled={saving}>
                  {saving ? 'Zapisywanie...' : (editingFactory ? 'Zapisz zmiany' : 'Dodaj fabrykę')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia */}
      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Usuń fabrykę</h3>
              <button style={s.closeBtn} onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <p style={{ padding: '0 24px 16px', color: '#555' }}>
              Czy na pewno chcesz usunąć fabrykę <strong>{deleteConfirm.nazwa}</strong>?
              Tej operacji nie można cofnąć.
            </p>
            <div style={{ ...s.modalFooter, borderTop: '1px solid #f0f0f0' }}>
              <button style={s.btnCancel} onClick={() => setDeleteConfirm(null)}>Anuluj</button>
              <button style={s.btnDeleteConfirm} onClick={() => handleDelete(deleteConfirm.id)}>
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '32px', color: '#e6edf3' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#e6edf3' },
  pageSubtitle: { margin: 0, color: '#8b949e', fontSize: '14px' },
  btnPrimary: { padding: '10px 20px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  btnCancel: { padding: '10px 20px', background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  btnEdit: { padding: '5px 10px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' },
  btnDelete: { padding: '5px 10px', background: '#2d1b1b', color: '#ff7875', border: '1px solid #5c2323', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  btnDeleteConfirm: { padding: '10px 20px', background: '#ff4d4f', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  errorBanner: { background: '#2d1b1b', border: '1px solid #5c2323', color: '#ff7875', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
  loading: { textAlign: 'center', padding: '60px', color: '#8b949e' },
  empty: { textAlign: 'center', padding: '60px', color: '#8b949e' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyText: { fontSize: '16px', marginBottom: '20px' },
  empty2: { color: '#484f58' },
  tableWrapper: { background: '#161b22', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.4)', border: '1px solid #30363d' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', background: '#0d1117', textAlign: 'left', borderBottom: '1px solid #30363d', fontWeight: '600', fontSize: '13px', color: '#8b949e' },
  td: { padding: '12px 16px', borderBottom: '1px solid #21262d', fontSize: '14px', verticalAlign: 'top', color: '#c9d1d9' },
  trEven: { background: '#161b22' },
  trOdd: { background: '#0d1117' },
  noteText: { color: '#8b949e', fontSize: '13px' },
  actions: { display: 'flex' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#161b22', borderRadius: '10px', width: '100%', maxWidth: '520px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: '1px solid #30363d' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #30363d' },
  modalTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#e6edf3' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#8b949e', lineHeight: 1 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px' },
  field: { padding: '12px 24px 0' },
  label: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#c9d1d9' },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #30363d', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', background: '#0d1117', color: '#c9d1d9' },
  errorText: { color: '#ff7875', fontSize: '13px', margin: '8px 24px 0' },
};
